import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useReactFlow } from "@xyflow/react";
import {
  canvasAtom,
  edgesAtom,
  isAiPageAtom,
  nodesAtom,
  startStreamingProgressSetter,
  togglesAtomResettable,
  zslAtom,
} from "@zapier/canvas-state";
import { ComposerProvider } from "@zapier/agent-system";
import { CopilotProvider } from "@zapier/ai-toolkit/client";
import { parseCanvasDataFromZSL } from "@zapier/streaming/src/utils/zslToCanvas";
import { zodSchema } from "@ai-sdk/ui-utils";
/**
 * TEMPORARY TYPE DECLARATION
 * This is a temporary solution to fix type errors with @zapier/zsl-creator-schemas
 * This should be replaced with proper type declarations from the package in the future
 */
// @ts-ignore
import { ZslSchemaForApi } from "@zapier/zsl-creator-schemas";
import { z } from "zod";
import { useSyncZslStreamToOutline } from "@zapier/streaming/src/hooks/useSyncZslStreamToOutline";
import { useRef } from "react";
import { Tool } from "ai";
import {
  outlineAtom,
  partialOutlineAtom,
} from "@zapier/streaming/src/atoms/outlineAtom";
import {
  getZapDescription,
  getZapExecute,
  GetZapParams,
} from "../../tools/zapTools/getZap";
import {
  needsApiDescription,
  needsApiExecute,
  NeedsApiParams,
} from "../../tools/zapTools/needsApi";
import {
  choicesApiDescription,
  choicesApiExecute,
  ChoicesApiParams,
} from "../../tools/zapTools/choicesApi";
import {
  authenticationCheckExecute,
  CheckAuthParams,
  authenticationCheckDescription,
} from "../../tools/zapTools/authenticationCheck";
import { renderAnnotationNodesSetter } from "../../tools/tour/renderAnnotationNodesSetter";
import {
  updateZapExecute,
  UpdateZapParams,
  updateZapDescription,
} from "../../tools/zapTools/updateZap";
import {
  validateZSLExecute,
  ValidateZSLParamsSchema,
  validateZSLDescription,
} from "../../tools/zslTools/validateZsl";
import { adaptCanvasToolsToAiToolkit } from "./utils/aiToolkitAdapter";

// Constants for tour description character limits
const DESCRIPTION_MIN_CHARS = 100;
const DESCRIPTION_MAX_CHARS = 144;

export const tourStepSchema = z.object({
  header: z.string(),
  description: z.string().min(DESCRIPTION_MIN_CHARS).max(DESCRIPTION_MAX_CHARS),
  parentId: z.string(),
  stepNumber: z.number().int().min(0),
});

export type ToolWithInclude = Tool & {
  shouldInclude?: () => boolean;
};

// Custom hook for creating a promise-based submission handler
const useSubmitAndWait = (config: {
  api: string;
  onError?: (error: string) => void;
}) => {
  const promiseRef = useRef<{
    resolve: (value: unknown) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  const { handleSubmit } = useSyncZslStreamToOutline({
    api: config.api,
    async onComplete(zsl: any) {
      if (promiseRef.current) {
        promiseRef.current.resolve(zsl);
        promiseRef.current = null;
      }
    },
    onError(error: string) {
      if (config.onError) {
        config.onError(error);
      }
      if (promiseRef.current) {
        promiseRef.current.reject(error);
        promiseRef.current = null;
      }
    },
  });

  const submitAndWait = (prompt: string) => {
    return new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
      handleSubmit(prompt);
    });
  };

  return submitAndWait;
};

/**
 * CanvasComposerHydrator is placed as a child of CanvasHydrator to ensure immediate access to:
 * - zsl data
 * - canvas metadata
 * - node data
 * - edge data
 *
 * This placement prevents any data transition states (like placeholder to real data)
 * and ensures the composer has all required data from initial render.
 */
export function CanvasComposerHydrator({
  children,
}: {
  children: React.ReactNode;
}) {
  const { fitView, setViewport } = useReactFlow();
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);
  const [zsl, setZsl] = useAtom(zslAtom);
  const [canvas, setCanvas] = useAtom(canvasAtom);
  const setOutline = useSetAtom(outlineAtom);
  const setPartialOutline = useSetAtom(partialOutlineAtom);
  const startProgressAnimation = useSetAtom(startStreamingProgressSetter);
  const isAiPage = useAtomValue(isAiPageAtom);
  const toggles = useAtomValue(togglesAtomResettable);
  const isFieldMappingComposer = toggles.fieldMappingComposer;
  const renderAnnotationNodes = useSetAtom(renderAnnotationNodesSetter);

  const includeFieldMappingTools = (): boolean => {
    return isFieldMappingComposer;
  };

  const submitAndWait = useSubmitAndWait({
    api: "/api/zsl/v2/combined_stream",
    onError: (error: string) => console.error("Error:", error),
  });

  const resetCanvas = () => {
    setNodes([]);
    setCanvas((prev) => ({
      ...prev,
      nodes: [],
      edges: [],
    }));
    setOutline("");
    setPartialOutline("");
    setEdges([]);
    setZsl(undefined);
    setViewport({ x: 0, y: 0, zoom: 1 });
  };

  const canvasTools: Record<string, ToolWithInclude> = {
    edit_system: {
      description: `This tool is used to:
        - Edit table, interface, and chatbot assets.
        - Add new tables and interfaces.
        - Remove any assets.

        ## Important
        This tool should not be used to:
        - Edit or add zaps.
        - Existing zaps MUST be preserved unchanged in your response.

        When calling this tool, make sure to provide the entire new system including:
        1. Your edits to tables, interfaces, or chatbots
        2. All existing zaps exactly as they are (do not modify or omit them)
        3. Any other existing components that should be preserved

        Your response must contain the complete system state, not just the changed parts.
        `,
      parameters: ZslSchemaForApi,
      shouldInclude: () => isAiPage,
      execute: async (args: any) => {
        setZsl(args);
        const nodesAndEdges = parseCanvasDataFromZSL(args, {
          canvas: {
            name: canvas.name || "Untitled Canvas",
            description: canvas.description || "",
            nodes: nodes as any,
            edges: edges as any,
          },
          warnings: [],
        });
        if (nodesAndEdges) {
          const {
            canvas: { nodes, edges },
          } = nodesAndEdges;
          setNodes(nodes as any);
          setEdges(edges as any);
          fitView();
        }
        return {
          content: [{ type: "text", text: "Canvas updated" }],
        };
      },
    },
    create_system: {
      description: ` The result of this tool will replace the current system.

        ## ALWAYS USE THIS TOOL FOR:
        - ANY changes involving zaps (new or edited)
        - Larger or complex system changes
        - Creating new systems
        - When edit_system is failing

        ## When NOT to use this tool:
        - Simple edits to fields on tables, interfaces, or chatbots
        - Adding new fields to existing tables, interfaces, or chatbots
        - Removing fields from tables, interfaces, or chatbots
        - Simple addition or removal of tables, interfaces, or chatbots that don't affect zaps
        
        Note: This tool completely replaces the system state with a new version created from your prompt.
        `,
      parameters: z.object({
        prompt: z
          .string()
          .describe(
            "A improved version of the users prompt to clarify users intent. This prompt will be used to update the system by another agent ",
          ),
      }),

      shouldInclude: () => isAiPage,
      execute: async (args: any) => {
        resetCanvas();
        startProgressAnimation();
        const zsl = await submitAndWait(args.prompt);
        return {
          content: [
            {
              type: "text",
              text: `System updated with zsl: ${JSON.stringify(zsl)}`,
            },
          ],
        };
      },
    },
    tour: {
      description: `AI-generated onboarding tour for the user's canvas. Generate a list of steps based on the annotation nodes schema to guide the user through the canvas. 
      VERY IMPORTANT: 
      1. The first step must be an intro step and stepNumber must be 0
      2. Each step must have a parentId that matches an existing node ID in the canvas
      3. There should be an annotation step for each node in the canvas plus the intro step (stepNumber: 0)
      4. Each step's description MUST be between ${DESCRIPTION_MIN_CHARS}-${DESCRIPTION_MAX_CHARS} characters
      5. Keep descriptions concise but informative - focus on the key purpose of each node
      6. Each step's header MUST be in sentence case: capitalize the first letter of the first word, and keep proper nouns, acronyms, and product names capitalized (e.g., "Zapier", "Zap", "Google Sheets").
         For example:
         - "notification and task creation zap" → "Notification and task creation Zap"
         - "leads table (zapier tables)" → "Leads table (Zapier Tables)"
         - "using google sheets" → "Using Google Sheets"
      7. The word "Zap" must always be capitalized as "Zap" in both the header and description, regardless of its position in the sentence.
      `,
      parameters: z.object({
        tourStepsData: z.array(tourStepSchema),
      }),
      shouldInclude: () => true,
      execute: async ({ tourStepsData }) => {
        return renderAnnotationNodes({ tourStepsData, fitView });
      },
    },
    get_zap: {
      description: getZapDescription,
      parameters: GetZapParams,
      shouldInclude: includeFieldMappingTools,
      execute: async (args: any) => {
        return getZapExecute(args);
      },
    },
    needs_api: {
      description: needsApiDescription,
      parameters: NeedsApiParams,
      shouldInclude: includeFieldMappingTools,
      execute: async (args: any) => {
        return needsApiExecute(args);
      },
    },
    choices_api: {
      description: choicesApiDescription,
      parameters: ChoicesApiParams,
      shouldInclude: includeFieldMappingTools,
      execute: async (args: any) => {
        return choicesApiExecute(args);
      },
    },
    authentication_check: {
      description: authenticationCheckDescription,
      parameters: CheckAuthParams,
      shouldInclude: includeFieldMappingTools,
      execute: async (args: any) => {
        return authenticationCheckExecute(args);
      },
    },
    update_zap: {
      description: updateZapDescription,
      parameters: UpdateZapParams,
      shouldInclude: includeFieldMappingTools,
      execute: async (args: any) => {
        return updateZapExecute(args);
      },
    },
    validate_zsl: {
      description: validateZSLDescription,
      parameters: ValidateZSLParamsSchema,
      shouldInclude: includeFieldMappingTools,
      execute: async (args: any) => {
        return validateZSLExecute(args);
      },
    },
  };

  const filteredTools = (
    tools: Record<string, ToolWithInclude>,
  ): Record<string, Tool> => {
    return Object.fromEntries(
      Object.entries(tools).filter(([, tool]) => {
        let shouldIncludeTool = true;
        if ("shouldInclude" in tool && tool.shouldInclude) {
          shouldIncludeTool = tool.shouldInclude();
        }
        return shouldIncludeTool;
      }),
    );
  };

  const filteredToolsResult = filteredTools(canvasTools);

  // Feature-flagged migration: Use ai-toolkit when aiCobuildCopilot is enabled
  if (toggles.aiCobuildCopilot) {
    const adaptedTools = adaptCanvasToolsToAiToolkit(canvasTools);

    return (
      <CopilotProvider
        api="/api/zsl/chat/completionsv2"
        clientTools={adaptedTools}
        copilotId="canvas-copilot"
        conversationId={`canvas-${canvas.id || "default"}`}
        maxSteps={6}
        body={{
          additionalData: {
            zsl: zsl || {},
            canvas: canvas || {},
            nodes,
            edges,
          },
        }}
        initialMessages={[
          {
            id: "canvas-welcome",
            role: "assistant",
            content:
              "I'm here to help you build and edit workflows on your canvas. I can help you create systems, edit existing components, generate tours, and work with Zaps. What would you like to do?",
          },
        ]}
        onError={(error: any) => {
          console.error("Canvas copilot error:", error);
        }}
      >
        {children}
      </CopilotProvider>
    );
  }

  // Existing agent-system implementation
  return (
    <ComposerProvider
      orchestratorHost="canvas"
      tools={Object.entries(filteredToolsResult).reduce(
        (acc, [key, tool]) => {
          const toolWithModifiedParameters = {
            ...tool,
            parameters: zodSchema(tool.parameters).jsonSchema,
          };
          acc[key] = toolWithModifiedParameters;
          return acc;
        },
        {} as Record<string, Tool>,
      )}
      initialAdditionalData={{
        zsl: zsl || {},
        canvas: canvas || {},
        nodes,
        edges,
      }}
    >
      {children}
    </ComposerProvider>
  );
}
